/**
 * Atlas telemetry sink — Phase 3.
 *
 * Default: a console sink (only in non-production).
 * Runtime override: call `setAtlasTelemetrySink()` from app bootstrap to
 *   forward events to an analytics vendor (e.g. Segment, PostHog, Mixpanel).
 *   This is intentionally vendor-agnostic — the feature module never imports
 *   from a vendor SDK.
 * Auto-detect: if `window.__atlasTelemetry` is defined at import time (e.g.
 *   injected by a script tag), it is used as the sink. This lets an analytics
 *   wrapper be wired without touching Atlas code.
 * Optional server mirror: when `enableAtlasServerMirror()` is called, events
 *   are additionally POSTed to `/api/atlas/events` via the shared `apiFetch`
 *   client. The server endpoint is out-of-scope for Phase 3 and must be
 *   implemented separately (table: `atlas_events`). Requests are fire-and
 *   -forget; failures never surface to the UI.
 */
import { apiFetch } from '../../shared/apiClient';
import type { AtlasTelemetryEvent, AtlasTelemetryPayload } from './atlas.types';

type TelemetrySink = (event: AtlasTelemetryEvent, payload: AtlasTelemetryPayload) => void;

declare global {
  interface Window {
    __atlasTelemetry?: TelemetrySink;
  }
}

const consoleSink: TelemetrySink = (event, payload) => {
  if (typeof window === 'undefined') return;
  try {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug(`[atlas] ${event}`, payload);
    }
  } catch {
    // ignore
  }
};

function pickInitialSink(): TelemetrySink {
  if (typeof window !== 'undefined' && typeof window.__atlasTelemetry === 'function') {
    return window.__atlasTelemetry;
  }
  return consoleSink;
}

let currentSink: TelemetrySink = pickInitialSink();
let serverMirrorEnabled = false;

export function setAtlasTelemetrySink(sink: TelemetrySink | null): void {
  currentSink = sink ?? (() => undefined);
}

/**
 * Enable fire-and-forget server mirror of Atlas events. Requires a server-side
 * POST /api/atlas/events endpoint — see phase3-telemetry in the plan.
 */
export function enableAtlasServerMirror(enabled: boolean = true): void {
  serverMirrorEnabled = enabled;
}

async function mirrorToServer(
  event: AtlasTelemetryEvent,
  payload: AtlasTelemetryPayload
): Promise<void> {
  if (!serverMirrorEnabled) return;
  try {
    await apiFetch('/api/atlas/events', {
      method: 'POST',
      body: JSON.stringify({ event, payload, ts: Date.now() }),
    });
  } catch {
    // Telemetry never throws into the UI.
  }
}

export function emitAtlas(event: AtlasTelemetryEvent, payload: AtlasTelemetryPayload = {}): void {
  try {
    currentSink(event, payload);
  } catch {
    // never let telemetry throw
  }
  // Deliberately not awaited: telemetry is fire-and-forget.
  void mirrorToServer(event, payload);
}
